
import { useMemo, useRef } from 'react';
import { Coordinates, ChargingStation, BathingPlace, RoadBarrier } from '../types';
import { getDistance } from '../services/geometryUtils';

// ───────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────

export type ContextualAlertType = 'charger' | 'bathing' | 'barrier' | 'fire';

export interface ContextualAlert {
  id: string;
  type: ContextualAlertType;
  title: string;
  subtitle?: string;
  distanceM: number;
  iconName: 'Plug' | 'Waves' | 'ShieldAlert' | 'Flame';
  color: string;       // tailwind text color
  bgColor: string;     // tailwind bg color
  borderColor: string; // tailwind border color
}

export interface FireDangerInput {
  isInDangerZone: boolean;
  isGeneralBanActive: boolean;
  highestSeverity: 'Yellow' | 'Orange' | 'Red' | null;
}

interface UseContextualAlertsParams {
  active: boolean;
  userPosition: Coordinates | null;
  chargers: ChargingStation[];
  bathingPlaces: BathingPlace[];
  barriers: RoadBarrier[];
  fireDanger: FireDangerInput | null;
}

// ───────────────────────────────────────────────────
// Thresholds
// ───────────────────────────────────────────────────

const CHARGER_RADIUS_M  = 5000;
const BATHING_RADIUS_M  = 3000;
const BARRIER_RADIUS_M  = 500;
const MAX_ALERTS         = 2;

// Summer months (May–September) for bathing relevance
const isSummerMonth = () => {
  const m = new Date().getMonth(); // 0-indexed
  return m >= 4 && m <= 8;
};

// ───────────────────────────────────────────────────
// Hook
// ───────────────────────────────────────────────────

export function useContextualAlerts({
  active,
  userPosition,
  chargers,
  bathingPlaces,
  barriers,
  fireDanger,
}: UseContextualAlertsParams): ContextualAlert[] {
  // Track dismissed alert IDs so user can dismiss and they don't reappear immediately
  const dismissedRef = useRef<Set<string>>(new Set());

  return useMemo(() => {
    if (!active || !userPosition) return [];

    const lat = userPosition.latitude;
    const lon = userPosition.longitude;
    const alerts: ContextualAlert[] = [];

    // ── Fire danger (highest priority, no distance) ──
    if (fireDanger) {
      if (fireDanger.isInDangerZone && fireDanger.highestSeverity) {
        const sevLabel = fireDanger.highestSeverity === 'Red' ? 'Høy brannfare'
          : fireDanger.highestSeverity === 'Orange' ? 'Brannfare'
          : 'Moderat brannfare';
        alerts.push({
          id: 'fire-zone',
          type: 'fire',
          title: sevLabel,
          subtitle: 'Bålforbudsperiode kan gjelde',
          distanceM: 0,
          iconName: 'Flame',
          color: fireDanger.highestSeverity === 'Red' ? 'text-red-600' : 'text-orange-600',
          bgColor: fireDanger.highestSeverity === 'Red' ? 'bg-red-50' : 'bg-orange-50',
          borderColor: fireDanger.highestSeverity === 'Red' ? 'border-red-300' : 'border-orange-300',
        });
      } else if (fireDanger.isGeneralBanActive) {
        alerts.push({
          id: 'fire-ban',
          type: 'fire',
          title: 'Generelt bålforbud',
          subtitle: '15. apr – 15. sep',
          distanceM: 0,
          iconName: 'Flame',
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-300',
        });
      }
    }

    // ── Barriers (< 500m) ──
    let nearestBarrier: { item: RoadBarrier; dist: number } | null = null;
    for (const b of barriers) {
      const d = getDistance(lat, lon, b.location.latitude, b.location.longitude);
      if (d <= BARRIER_RADIUS_M && (!nearestBarrier || d < nearestBarrier.dist)) {
        nearestBarrier = { item: b, dist: d };
      }
    }
    if (nearestBarrier) {
      const b = nearestBarrier.item;
      const subtitle = b.openingMethod || b.type || undefined;
      alerts.push({
        id: `barrier-${b.id}`,
        type: 'barrier',
        title: `Bom ${Math.round(nearestBarrier.dist)}m unna`,
        subtitle,
        distanceM: nearestBarrier.dist,
        iconName: 'ShieldAlert',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-300',
      });
    }

    // ── Chargers (< 5km) ──
    let nearestCharger: { item: ChargingStation; dist: number } | null = null;
    for (const c of chargers) {
      const d = getDistance(lat, lon, c.location.latitude, c.location.longitude);
      if (d <= CHARGER_RADIUS_M && (!nearestCharger || d < nearestCharger.dist)) {
        nearestCharger = { item: c, dist: d };
      }
    }
    if (nearestCharger) {
      const c = nearestCharger.item;
      const distLabel = nearestCharger.dist < 1000
        ? `${Math.round(nearestCharger.dist)}m`
        : `${(nearestCharger.dist / 1000).toFixed(1)}km`;
      const connectorStr = c.connectors?.length ? c.connectors.slice(0, 2).join(', ') : undefined;
      alerts.push({
        id: `charger-${c.id}`,
        type: 'charger',
        title: `Lader ${distLabel} unna`,
        subtitle: connectorStr || c.name || undefined,
        distanceM: nearestCharger.dist,
        iconName: 'Plug',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300',
      });
    }

    // ── Bathing places (< 3km, summer only) ──
    if (isSummerMonth()) {
      let nearestBathing: { item: BathingPlace; dist: number } | null = null;
      for (const bp of bathingPlaces) {
        const d = getDistance(lat, lon, bp.location.latitude, bp.location.longitude);
        if (d <= BATHING_RADIUS_M && (!nearestBathing || d < nearestBathing.dist)) {
          nearestBathing = { item: bp, dist: d };
        }
      }
      if (nearestBathing) {
        const bp = nearestBathing.item;
        const distLabel = nearestBathing.dist < 1000
          ? `${Math.round(nearestBathing.dist)}m`
          : `${(nearestBathing.dist / 1000).toFixed(1)}km`;
        const tempStr = bp.waterTemperature != null ? ` — ${bp.waterTemperature}°C` : '';
        alerts.push({
          id: `bathing-${bp.id}`,
          type: 'bathing',
          title: `Badeplass ${distLabel}${tempStr}`,
          subtitle: bp.name || undefined,
          distanceM: nearestBathing.dist,
          iconName: 'Waves',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
        });
      }
    }

    // Prioritize: fire > barrier > charger > bathing, max 2
    return alerts.slice(0, MAX_ALERTS);
  }, [active, userPosition, chargers, bathingPlaces, barriers, fireDanger]);
}
